import PropTypes, { object } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Avatar, Collapse, Grid, IconButton, List, ListItem, ListItemAvatar, ListItemIcon, ListItemText, ListSubheader, Paper, styled, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { ExpandMore, ExpandLess, SubdirectoryArrowRight, Remove, CheckCircle, ReportProblem } from '@mui/icons-material';
import React, { useState } from 'react';

const formatDate = (dateToFormat) => {
  if (dateToFormat) {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateToFormat).toLocaleDateString('it-IT', options);
  }
  return '';
};

const checkValidity = (validFrom, validTo) => {
  const today = new Date();
  if (validFrom && today < new Date(validFrom)) return false;
  if (validTo && today > new Date(validTo)) return false;
  return true;
};

function ChildrenLotInformation({ orgId, orgName, authority, code, info = {}, validFrom, validTo, children = [], isRoot = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }));

  const StyledTableCell = styled(TableCell)(({ align }) => ({
    fontSize: 15,
    paddingLeft: 0,
    textAlign: align || 'right',
  }));

  return (
    <>
      <ListItem key={`${authority}:${code}`} sx={{ p: 0 }} divider>
        <ListItemAvatar>
          <Avatar sx={{ bgcolor: 'white', color: 'black', width: 24, height: 24 }}>{!isRoot ? <SubdirectoryArrowRight data-testid="subdirectory-arrow-right" /> : <Remove />}</Avatar>
        </ListItemAvatar>
        <ListItemAvatar>
          <Avatar sx={{ bgcolor: 'white', color: 'green' }}>
            <CheckCircle data-testid="check-circle" />
          </Avatar>
        </ListItemAvatar>

        <ListItemText primaryTypographyProps={{ display: 'inline' }} secondaryTypographyProps={{ display: 'inline', ml: 2 }} primary={authority} secondary={code} />
        {children && children.length > 0 && (
          <ListItemIcon>
            <IconButton onClick={() => setOpen(!open)}>{open ? <ExpandLess /> : <ExpandMore data-testid="expand-more" />}</IconButton>
          </ListItemIcon>
        )}
      </ListItem>

      <List component="div" disablePadding sx={{ ml: 3 }} subheader={<ListSubheader sx={{ p: 0, lineHeight: '36px' }}>{t('products.lots.info')}</ListSubheader>}>
        <ListItem sx={{ p: 0 }} divider>
          <ListItemText primaryTypographyProps={{ display: 'inline' }} primary={orgName} secondary={t('products.lots.org')} />
        </ListItem>
        {(info?.type || info?.recycledPlasticPercentage) && (
          <ListItem sx={{ p: 0 }} divider>
            <Grid container>
              {info?.type && (
                <Grid item xs={6}>
                  <ListItemText primaryTypographyProps={{ display: 'inline' }} primary={info.type} secondary={t('products.lots.type')} />
                </Grid>
              )}
              {info?.recycledPlasticPercentage >= 0 && (
                <Grid item xs={6}>
                  <ListItemText primaryTypographyProps={{ display: 'inline' }} primary={`${parseFloat((info.recycledPlasticPercentage * 100).toFixed(13))}%`} secondary={t('products.lots.recycledPlasticPercentage')} />
                </Grid>
              )}
            </Grid>
          </ListItem>
        )}
        {(info?.recycling || info?.recyclingCode) && (
          <ListItem sx={{ p: 0 }} divider>
            <Grid container>
              {info?.recycling && (
                <Grid item xs={6}>
                  <ListItemText primaryTypographyProps={{ display: 'inline' }} primary={info.recycling} secondary={t('products.lots.recycling')} />
                </Grid>
              )}
              {info?.recyclingCode && (
                <Grid item xs={6}>
                  <ListItemText primaryTypographyProps={{ display: 'inline' }} primary={info.recyclingCode} secondary={t('products.lots.recyclingCode')} />
                </Grid>
              )}
            </Grid>
          </ListItem>
        )}
        {info?.materials && info.materials.length > 0 && (
          <ListItem sx={{ p: 0 }} divider>
            <ListItemText
              primaryTypographyProps={{ display: 'inline' }}
              primary={
                <TableContainer sx={{ width: 'fit-content', maxWidth: '100%' }}>
                  <Table size="small">
                    <TableBody>
                      {info.materials.map((material) => (
                        <StyledTableRow>
                          <StyledTableCell component="th" scope="row" align="left">
                            {material?.description ? material.description : '-'}
                          </StyledTableCell>
                          <StyledTableCell align="left">{material?.type ? material.type : '-'}</StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              }
              secondary={t('products.lots.rawMaterials')}
            />
          </ListItem>
        )}
        <ListItem sx={{ p: 0, color: checkValidity(validFrom, validTo) ? undefined : '#ff7022' }} divider data-testid="validity">
          <ListItemText primaryTypographyProps={{ display: 'inline' }} primary={`${formatDate(validFrom)} - ${formatDate(validTo)}`} secondary={t('products.lots.validity')} />
          {!checkValidity(validFrom, validTo) && (
            <ListItemAvatar>
              <Avatar sx={{ bgcolor: 'white', color: '#ff7022' }}>{<ReportProblem data-testid="report-problem" />}</Avatar>
            </ListItemAvatar>
          )}
        </ListItem>
      </List>
      {children && children.length > 0 && (
        <Collapse in={open}>
          <List component="div" disablePadding sx={{ ml: 3 }} subheader={<ListSubheader sx={{ p: 0, lineHeight: '36px' }}>{t('products.lots.subLots')}</ListSubheader>}>
            {children.map((children) => (
              <ChildrenLotInformation orgId={orgId} orgName={orgName} authority={children.authority} code={children.code} info={children.info} validFrom={children.valid_from} validTo={children.valid_to} children={children.children} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

const infoShape = PropTypes.shape({
  type: PropTypes.string.isRequired,
  surfaceWeight: PropTypes.number,
  pieceSurface: PropTypes.number,
  plasticPercentage: PropTypes.number,
  recycledPlasticPercentage: PropTypes.number,
  recycling: PropTypes.string,
  materials: PropTypes.arrayOf(object),
});

ChildrenLotInformation.propTypes = {
  orgId: PropTypes.number.isRequired,
  orgName: PropTypes.string.isRequired,
  authority: PropTypes.string.isRequired,
  code: PropTypes.string.isRequired,
  info: infoShape,
  validFrom: PropTypes.string,
  validTo: PropTypes.string,
  children: PropTypes.array,
  isRoot: PropTypes.bool,
};

export default ChildrenLotInformation;
